import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { DropResult } from '@hello-pangea/dnd'; // Případně adjustujte import dle vaší DnD knihovny

import '../../shared/ReactSelectDarkMode.css';
import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { usePermissions } from '../../shared/permissions';
import { useConfirm } from '../../shared/ConfirmContext';
import CategoryFormModal from './components/CategoryFormModal';
import CategoryTreePanel from './components/CategoryTreePanel';

export interface ProductCategory {
  id: number;
  name: string;
  priority: number;
  description?: string | null;
  color?: string | null;
  parent?: number | null;
}

export interface CategoryFormData {
  name: string;
  priority: number;
  description: string;
  color: string;
  parent: number | null;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const PAGE_SIZE = 50;

const ProductCategoryManagement: React.FC = () => {
  const { warehouseId } = useParams<{ warehouseId?: string }>();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [categoriesOverride, setCategoriesOverride] = useState<ProductCategory[] | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | null>(null);

  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    priority: 0,
    description: '',
    color: '#cccccc',
    parent: null,
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: loading,
  } = useInfiniteQuery<PaginatedResponse<ProductCategory>>({
    queryKey: ['product-categories', warehouseId, debouncedSearch],
    queryFn: ({ pageParam = 1 }) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        page_size: String(PAGE_SIZE),
        ordering: 'priority',
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      return api.getRaw(`warehouses/api/product-categories/?${params}`);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      (lastPage.results?.length ?? 0) === PAGE_SIZE ? allPages.length + 1 : undefined,
    staleTime: 30_000,
  });

  useRealtimeEvents((e) => {
    if (e.model === 'warehouses.productcategory') {
      queryClient.invalidateQueries({ queryKey: ['product-categories', warehouseId] });
    }
  });

  const categories = useMemo(
    () => data?.pages.flatMap((p) => p.results ?? []) ?? [],
    [data]
  );
  const totalCount = data?.pages[0]?.count ?? null;
  const displayCategories = categoriesOverride ?? categories;

  const categoryById = useMemo<Record<number, ProductCategory>>(
    () => Object.fromEntries(displayCategories.map((c) => [c.id, c])),
    [displayCategories]
  );

  const childrenByParent = useMemo<Record<number, ProductCategory[]>>(() => {
    const map: Record<number, ProductCategory[]> = {};
    displayCategories.forEach((c) => {
      if (c.parent) {
        if (!map[c.parent]) map[c.parent] = [];
        map[c.parent].push(c);
      }
    });
    return map;
  }, [displayCategories]);

  const rootCategories = useMemo<ProductCategory[]>(
    () => displayCategories.filter((c) => !c.parent),
    [displayCategories]
  );

  const orphanedChildren = useMemo<ProductCategory[]>(
    () => displayCategories.filter((c) => Boolean(c.parent && !categoryById[c.parent])),
    [displayCategories, categoryById]
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 150 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const resetFormData = useCallback(() => {
    setFormData({ name: '', priority: 0, description: '', color: '#cccccc', parent: null });
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const updateFormData = <K extends keyof CategoryFormData>(field: K, value: CategoryFormData[K]) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const addMutation = useMutation({
    mutationFn: () => api.post('warehouses/api/product-categories/', formData),
    onSuccess: () => {
      setShowAddModal(false);
      resetFormData();
      queryClient.invalidateQueries({ queryKey: ['product-categories', warehouseId] });
    },
    onError: () => setError(t('warehouses.productCategories.addError')),
  });

  const editMutation = useMutation({
    mutationFn: () => {
      if (!selectedCategory) throw new Error('No category selected for edit');
      return api.put(`warehouses/api/product-categories/${selectedCategory.id}/`, formData);
    },
    onSuccess: () => {
      setShowEditModal(false);
      setSelectedCategory(null);
      resetFormData();
      queryClient.invalidateQueries({ queryKey: ['product-categories', warehouseId] });
    },
    onError: () => setError(t('warehouses.productCategories.editError')),
  });

  const deleteMutation = useMutation({
    mutationFn: (categoryId: number) => api.delete(`warehouses/api/product-categories/${categoryId}/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['product-categories', warehouseId] }),
    onError: () => setError(t('warehouses.productCategories.deleteError')),
  });

  const handleAddCategory = (e: React.FormEvent) => {
    e.preventDefault();
    addMutation.mutate();
  };

  const handleEditCategory = (e: React.FormEvent) => {
    e.preventDefault();
    editMutation.mutate();
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (!(await confirm(t('common.confirm')))) return;
    deleteMutation.mutate(categoryId);
  };

  const openEditModal = (category: ProductCategory) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      priority: category.priority || 0,
      description: category.description || '',
      color: category.color || '#cccccc',
      parent: category.parent ?? null,
    });
    setShowEditModal(true);
  };

  const updateCategoriesPriority = async (reorderedCategories: ProductCategory[]) => {
    try {
      const updates = reorderedCategories.map((category, index) => ({
        id: category.id,
        priority: (index + 1) * 10,
        name: category.name,
        description: category.description,
        color: category.color,
        parent: category.parent ?? null,
      }));

      const priorityById = Object.fromEntries(updates.map((u) => [u.id, u.priority]));
      setCategoriesOverride((prev) =>
        (prev ?? []).map((c) =>
          priorityById[c.id] !== undefined ? { ...c, priority: priorityById[c.id] } : c
        )
      );

      await api.post('warehouses/api/product-categories/bulk_reorder/', updates);

      // Refetch the real order from the server before dropping the optimistic
      // override below — otherwise the list would flash back to the stale
      // (pre-drag) order for the moment between clearing the override and the
      // background refetch completing.
      await queryClient.invalidateQueries({ queryKey: ['product-categories', warehouseId] });
    } catch (err) {
      console.error('Error updating category priorities:', err);
      setError(t('warehouses.productCategories.reorderError'));
      queryClient.invalidateQueries({ queryKey: ['product-categories', warehouseId] });
    } finally {
      setCategoriesOverride(null);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, combine, draggableId } = result;
    const srcId = source.droppableId;

    if (combine && draggableId.startsWith('group-')) {
      const movedId = parseInt(draggableId.replace('group-', ''), 10);
      const targetId = parseInt(combine.draggableId.replace('group-', ''), 10);
      if (isNaN(movedId) || isNaN(targetId) || movedId === targetId) return;
      if (childrenByParent[movedId]?.length > 0) return;

      const descendantIds = new Set<number>();
      const queue = [movedId];
      while (queue.length) {
        const pid = queue.shift()!;
        (childrenByParent[pid] || []).forEach((child) => {
          descendantIds.add(child.id);
          queue.push(child.id);
        });
      }
      if (descendantIds.has(targetId)) return;

      const movedCat = categories.find((c) => c.id === movedId);
      if (!movedCat) return;
      const updatedCat: ProductCategory = { ...movedCat, parent: targetId };

      const newRoots = rootCategories.filter((r) => r.id !== movedId);
      const newCbp: Record<number, ProductCategory[]> = {};
      Object.keys(childrenByParent).forEach((pidStr) => {
        const pid = parseInt(pidStr, 10);
        newCbp[pid] = [...childrenByParent[pid]];
      });
      newCbp[targetId] = [...(newCbp[targetId] || []), updatedCat];

      const newList: ProductCategory[] = [];
      newRoots.forEach((root) => {
        newList.push(root);
        (newCbp[root.id] || []).forEach((child) => newList.push(child));
      });
      orphanedChildren.forEach((c) => newList.push(c));
      setCategoriesOverride(newList);
      updateCategoriesPriority(newList);
      return;
    }

    if (!destination && !combine && draggableId.startsWith('item-')) {
      const movedId = parseInt(draggableId.replace('item-', ''), 10);
      const movedCat = displayCategories.find((c) => c.id === movedId);
      if (!movedCat || !movedCat.parent) return;
      const updatedCat: ProductCategory = { ...movedCat, parent: null };
      const newCbp: Record<number, ProductCategory[]> = {};
      Object.keys(childrenByParent).forEach((pidStr) => {
        const pid = parseInt(pidStr, 10);
        newCbp[pid] = [...childrenByParent[pid]];
      });
      const srcParentId = movedCat.parent;
      newCbp[srcParentId] = (newCbp[srcParentId] || []).filter((c) => c.id !== movedId);
      const newRoots = [...rootCategories, updatedCat];
      const newList: ProductCategory[] = [];
      newRoots.forEach((root) => {
        newList.push(root);
        (newCbp[root.id] || []).forEach((child) => newList.push(child));
      });
      orphanedChildren.filter((c) => c.id !== movedId).forEach((c) => newList.push(c));
      setCategoriesOverride(newList);
      updateCategoriesPriority(newList);
      return;
    }

    if (!destination) return;
    const dstId = destination.droppableId;

    if (srcId === 'root-groups' && dstId === 'root-groups') {
      if (source.index === destination.index) return;
      const reorderedRoots = [...rootCategories];
      const [movedRoot] = reorderedRoots.splice(source.index, 1);
      reorderedRoots.splice(destination.index, 0, movedRoot);
      const newList: ProductCategory[] = [];
      reorderedRoots.forEach((root) => {
        newList.push(root);
        (childrenByParent[root.id] || []).forEach((child) => newList.push(child));
      });
      orphanedChildren.forEach((c) => newList.push(c));
      setCategoriesOverride(newList);
      updateCategoriesPriority(newList);
      return;
    }

    if (!draggableId.startsWith('item-')) return;
    const movedId = parseInt(draggableId.replace('item-', ''), 10);
    const movedCat = displayCategories.find((c) => c.id === movedId);
    if (!movedCat) return;

    const srcParentId =
      srcId.startsWith('children-') && srcId !== 'children-null'
        ? parseInt(srcId.replace('children-', ''), 10)
        : null;
    const dstParentId =
      dstId.startsWith('children-') && dstId !== 'children-null'
        ? parseInt(dstId.replace('children-', ''), 10)
        : null;

    if (srcParentId === dstParentId && source.index === destination.index) return;

    const updatedCat: ProductCategory = { ...movedCat, parent: dstParentId };

    const newCbp: Record<number, ProductCategory[]> = {};
    Object.keys(childrenByParent).forEach((pidStr) => {
      const pid = parseInt(pidStr, 10);
      newCbp[pid] = [...childrenByParent[pid]];
    });

    if (srcParentId !== null) {
      newCbp[srcParentId] = (newCbp[srcParentId] || []).filter((c) => c.id !== movedId);
    }
    if (dstParentId !== null) {
      const dstArr = (newCbp[dstParentId] || []).filter((c) => c.id !== movedId);
      dstArr.splice(destination.index, 0, updatedCat);
      newCbp[dstParentId] = dstArr;
    }

    let newRoots = rootCategories;
    if (dstParentId === null && srcParentId !== null) {
      newRoots = [...rootCategories, updatedCat];
    }

    const newList: ProductCategory[] = [];
    newRoots.forEach((root) => {
      newList.push(root.id === movedId ? updatedCat : root);
      (newCbp[root.id] || []).forEach((child) => newList.push(child));
    });
    orphanedChildren.filter((c) => c.id !== movedId).forEach((c) => newList.push(c));
    setCategoriesOverride(newList);
    updateCategoriesPriority(newList);
  };

  const backHref = warehouseId ? `/warehouses/stock/${warehouseId}` : '/warehouses/products';

  const addCategory = () => {
    resetFormData();
    setShowAddModal(true);
  };

  return (
    <PageShell
      pretitle={t('warehouses.title', 'Sklad')}
      title={t('warehouses.productCategories.title', 'Kategorie produktů')}
      description={t('warehouses.productCategories.subtitle', 'Správa kategórií a hierarchie produktov v katalógu.')}
      actions={
        <>
          <Link to={backHref} className="btn btn-outline-secondary">
            <i className="ti ti-arrow-left me-2" />
            {t('common.back')}
          </Link>
          {canManage && (
            <button className="btn btn-primary" onClick={addCategory}>
              <i className="ti ti-plus me-2" />
              {t('warehouses.categories.add', 'Přidat kategorii')}
            </button>
          )}
        </>
      }
    >
      {error && (
        <div className="alert alert-danger app-inline-alert" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <PageSection>
          <CenteredLoader />
        </PageSection>
      ) : categories.length === 0 ? (
        <PageSection>
          <EmptyState
            icon={<i className="ti ti-category app-empty-state__icon-glyph" aria-hidden="true" />}
            title={t('warehouses.categories.empty', 'Žádné kategorie')}
            message={searchQuery ? t('warehouses.categories.emptySearch') : t('warehouses.categories.emptyMessage')}
            action={
              canManage ? (
                <button className="btn btn-primary" onClick={addCategory}>
                  <i className="ti ti-plus me-2" />
                  {t('warehouses.categories.add', 'Přidat kategorii')}
                </button>
              ) : null
            }
          />
        </PageSection>
      ) : (
        <CategoryTreePanel
          categories={categories}
          totalCount={totalCount}
          rootCategories={rootCategories}
          childrenByParent={childrenByParent}
          orphanedChildren={orphanedChildren}
          canManage={canManage}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onClearSearch={() => setSearchQuery('')}
          onScroll={handleScroll}
          onDragEnd={handleDragEnd}
          onEdit={openEditModal}
          onDelete={handleDeleteCategory}
          isFetchingNextPage={isFetchingNextPage}
        />
      )}

      {showAddModal && (
        <CategoryFormModal
          mode="add"
          categories={categories}
          childrenByParent={childrenByParent}
          formData={formData}
          onChange={updateFormData}
          onClose={() => {
            setShowAddModal(false);
            resetFormData();
          }}
          onSubmit={handleAddCategory}
        />
      )}

      {showEditModal && selectedCategory && (
        <CategoryFormModal
          mode="edit"
          categories={categories}
          childrenByParent={childrenByParent}
          selectedCategory={selectedCategory}
          formData={formData}
          onChange={updateFormData}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCategory(null);
            resetFormData();
          }}
          onSubmit={handleEditCategory}
        />
      )}
    </PageShell>
  );
};

export default ProductCategoryManagement;
